import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Printer, Edit2, XCircle, Camera } from "lucide-react";
import { useState, useMemo, useEffect } from "react";

import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  useCustomerOrders,
  useOrderLabels,
  useOrderStatus,
} from "@/api/use-order";
import { logger } from "@/services/logger";
import { OrderStatus, Parcel } from "@/api.types";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import { type CarouselApi } from "@/components/ui/carousel";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

// Helper function to determine if any part of an order is editable
function isOrderEditable(status: OrderStatus): boolean {
  return ![
    OrderStatus.DELIVERED,
    OrderStatus.RETURNED,
    OrderStatus.PICKUP_CANCELLED,
    OrderStatus.DEPOT_RECEIVE_CANCELLED,
    OrderStatus.DEPOT_SORT_CANCELLED,
    OrderStatus.DEPOT_DISPATCH_CANCELLED,
    OrderStatus.DELIVERY_CANCELLED,
    OrderStatus.RETURN_CANCELLED,
    OrderStatus.CANCELLED,
  ].includes(status);
}

// Define types for tasks and proofs based on the admin app
interface ProofItem {
  id: string;
  url: string;
  type: "PHOTO" | "VIDEO";
  key: string;
  createdAt: string;
  updatedAt: string;
}

interface Proof {
  id: string;
  taskId: string;
  mediaItems?: ProofItem[];
  notes?: string;
}

interface Task {
  id: string;
  type:
    | "PICKUP"
    | "DELIVERY"
    | "DEPOT_RECEIVE"
    | "DEPOT_SORT"
    | "DEPOT_DISPATCH"
    | "RETURN";
  status: string;
  createdAt: string;
  updatedAt: string;
  proof?: Proof;
}

// Extend Order type to include tasks
interface CustomerOrder {
  id: string;
  orderNumber: string;
  status: string;
  recipientName: string;
  recipientEmail: string;
  recipientPhone: string;
  deliveryAddress: {
    line1: string;
    city: string;
  };
  parcels: any[];
  totalFees: number;
  createdAt: string;
  tasks?: Task[];
}

// Helper type for photo metadata
interface PhotoMeta {
  url: string;
  taskType: string;
  status: string;
  time: string;
  notes?: string;
}

// New OrderCard component for mobile view
interface OrderCardProps {
  order: CustomerOrder;
  isSelected: boolean;
  onSelect: (checked: boolean) => void;
  onPrintLabel: (orderId: string) => void;
  onEditOrder: (orderId: string) => void;
  onCancelOrder: (orderId: string) => void;
  onViewPhotos: (order: CustomerOrder) => void;
  onTrackingClick: (trackingNumber: string) => void;
  getTaskProofImages: (tasks: Task[]) => PhotoMeta[];
}

function OrderCard({
  order,
  isSelected,
  onSelect,
  onPrintLabel,
  onEditOrder,
  onCancelOrder,
  onViewPhotos,
  onTrackingClick,
  getTaskProofImages,
}: OrderCardProps) {
  const [expanded, setExpanded] = useState(false);
  const photoCount = order.tasks ? getTaskProofImages(order.tasks).length : 0;
  const hasPhotos = photoCount > 0;

  return (
    <div className="border rounded-lg mb-4 overflow-hidden bg-white">
      {/* Header section with checkbox, order number and status */}
      <div className="p-4 border-b flex items-center justify-between bg-gray-50">
        <div className="flex items-center gap-3">
          <Checkbox checked={isSelected} onCheckedChange={onSelect} />
          <div>
            <h3 className="font-medium">#{order.orderNumber}</h3>
            <p className="text-xs text-gray-500">
              {new Date(order.createdAt).toLocaleDateString()}
            </p>
          </div>
        </div>
        <Badge
          className={`${statusColors[order.status] || "bg-gray-500"} px-3 py-1`}
          variant="default"
        >
          {order.status.replace(/_/g, " ")}
        </Badge>
      </div>

      {/* Main content section */}
      <div className="p-4">
        {/* Recipient info */}
        <div className="mb-4">
          <div className="flex justify-between items-center mb-1">
            <h4 className="text-sm font-semibold text-gray-700">Recipient</h4>
            <button
              className="text-xs text-blue-600 px-2 py-1"
              onClick={() => setExpanded(!expanded)}
            >
              {expanded ? "Less" : "More"}
            </button>
          </div>
          <p className="font-medium mb-1">{order.recipientName}</p>
          {expanded && (
            <>
              <p className="text-sm text-gray-600 mb-1">
                <a
                  href={`mailto:${order.recipientEmail}`}
                  className="text-blue-600 underline"
                >
                  {order.recipientEmail}
                </a>
              </p>
              <p className="text-sm text-gray-600 mb-1">
                <a
                  href={`tel:${order.recipientPhone}`}
                  className="flex items-center text-blue-600 font-medium"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4 mr-1"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                    />
                  </svg>
                  {order.recipientPhone}
                </a>
              </p>
              <p className="text-sm text-gray-600">
                <a
                  href={`https://maps.google.com/?q=${encodeURIComponent(
                    `${order.deliveryAddress.line1}, ${order.deliveryAddress.city}`
                  )}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 underline"
                >
                  {order.deliveryAddress.line1}, {order.deliveryAddress.city}
                </a>
              </p>
            </>
          )}
        </div>

        {/* Parcels info */}
        <div className="mb-4">
          <h4 className="text-sm font-semibold text-gray-700 mb-2">
            {order.parcels.length}{" "}
            {order.parcels.length === 1 ? "parcel" : "parcels"}
          </h4>
          {expanded &&
            order.parcels.map((parcel: Parcel) => (
              <div key={parcel.id} className="text-sm mb-2">
                <span
                  className="text-blue-600 underline"
                  onClick={() => onTrackingClick(parcel.trackingNumber)}
                >
                  {parcel.trackingNumber}
                </span>
                <span className="text-sm text-gray-500 block">
                  {parcel.dimensions.length}x{parcel.dimensions.width}x
                  {parcel.dimensions.height}cm, {parcel.weight}kg
                </span>
              </div>
            ))}
        </div>

        {/* Price */}
        <div className="mb-4">
          <h4 className="text-sm font-semibold text-gray-700 mb-1">Total</h4>
          <p className="font-medium">${order.totalFees.toFixed(2)}</p>
        </div>

        {/* Photos section */}
        {hasPhotos && (
          <div className="mb-3">
            <h4 className="text-sm font-semibold text-gray-700 mb-2">
              {photoCount} {photoCount === 1 ? "photo" : "photos"}
            </h4>
            <div className="flex gap-3 overflow-x-auto pb-2">
              {getTaskProofImages(order.tasks ?? [])
                .slice(0, 4)
                .map((image: PhotoMeta, index) => (
                  <div
                    key={index}
                    className="relative w-16 h-16 flex-shrink-0 cursor-pointer"
                    onClick={() => onViewPhotos(order)}
                  >
                    <img
                      src={image.url}
                      alt={`${image.taskType} proof`}
                      className="w-full h-full object-cover rounded-md border border-gray-200"
                      onError={(e) => {
                        (e.target as HTMLImageElement).src =
                          "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjQiIGhlaWdodD0iMjQiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyIgZmlsbC1ydWxlPSJldmVub2RkIiBjbGlwLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0yNCAyNGgtMjR2LTI0aDI0djI0em0tMi0yMmgtMjB2MjBoMjB2LTIwem0tNC4xMTggMTQuMDY0Yy0uMzk5LjQxOS0uODM0LjY0MS0xLjI5OS42NDEtLjQ2NSAwLS45LS4yMjItMS4yOTktLjY0MS0uNDAxLS40MTgtLjYwMS0uOTAyLS42MDEtMS40NTEgMC0uNTM5LjIwMS0xLjAyNC42MDEtMS40NTIuMzk5LS40MTkuODM0LS42NDEgMS4yOTktLjY0MS40NjUgMCAuOS4yMjIgMS4yOTkuNjQxLjQwMS40MTguNjAxLjkwMi42MDEgMS40NTEgMCAuNTM5LS4yMDEgMS4wMjQtLjYwMSAxLjQ1MnptLTEuMjk5LTExLjA2NGMtMy4wMjEgMC01LjYwNiAxLjA2Ny03Ljc1NCAzLjItMi4xNDkgMi4xMzQtMy4yMjMgNC43MDgtMy4yMjMgNy43MjEgMCAzLjAyMiAxLjA3NCA1LjU5NyAzLjIyMyA3LjcyIDIuMTQ4IDIuMTM0IDQuNzMzIDMuMjAxIDcuNzU0IDMuMjAxIDMuMDIgMCA1LjYwNS0xLjA2NyA3Ljc1My0zLjIgMi4xNDktMi4xMzQgMy4yMjMtNC43MDkgMy4yMjMtNy43MjEgMC0zLjAyMi0xLjA3NC01LjU5Ny0zLjIyMy03LjcyMS0yLjE0OC0yLjEzMy00LjczMy0zLjItNy43NTMtMy4yem0wIDIuMTMzYzIuMzk1IDAgNC40NDIuODQ4IDYuMTQzIDIuNTMzIDEuNyAxLjY4NiAyLjU1IDMuNzI1IDIuNTUgNi4xMTcgMCAyLjM5Mi0uODUgNC40MzEtMi41NSA2LjExNy0xLjcwMSAxLjY4NS0zLjc0OCAyLjUzMy02LjE0MyAyLjUzMy0yLjM5NCAwLTQuNDQyLS44NDgtNi4xNDMtMi41MzMtMS43LTEuNjg2LTIuNTUtMy43MjUtMi41NS02LjExNyAwLTIuMzkyLjg1LTQuNDMxIDIuNTUtNi4xMTcgMS43MDEtMS42ODUgMy43NDktMi41MzMgNi4xNDMtMi41MzN6Ii8+PC9zdmc+";
                      }}
                    />
                  </div>
                ))}
              {photoCount > 4 && (
                <div
                  className="w-16 h-16 bg-gray-100 rounded-md flex items-center justify-center text-gray-500 border border-gray-200 flex-shrink-0 cursor-pointer"
                  onClick={() => onViewPhotos(order)}
                >
                  +{photoCount - 4}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Actions footer */}
      <div className="p-4 border-t flex justify-between items-center bg-gray-50">
        <div className="flex gap-3">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onPrintLabel(order.id)}
            disabled={!isOrderEditable(order.status as OrderStatus)}
            className="flex items-center"
          >
            <Printer className="h-4 w-4 mr-1" />
            <span className="text-xs">Print</span>
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onEditOrder(order.id)}
            disabled={!isOrderEditable(order.status as OrderStatus)}
            className="flex items-center"
          >
            <Edit2 className="h-4 w-4 mr-1" />
            <span className="text-xs">Edit</span>
          </Button>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => onCancelOrder(order.id)}
          disabled={
            ![
              OrderStatus.REGISTERED,
              OrderStatus.PICKUP_PENDING,
              OrderStatus.OUT_FOR_PICKUP,
            ].includes(order.status as OrderStatus)
          }
          className="text-red-600 flex items-center"
        >
          <XCircle className="h-4 w-4 mr-1" />
          <span className="text-xs">Cancel</span>
        </Button>
      </div>
    </div>
  );
}

const statusColors: Record<string, string> = {
  [OrderStatus.REGISTERED]: "bg-blue-500",
  [OrderStatus.PICKUP_PENDING]: "bg-yellow-500",
  [OrderStatus.OUT_FOR_PICKUP]: "bg-blue-500",
  [OrderStatus.PICKUP_ASSIGNED]: "bg-blue-500",
  [OrderStatus.PICKED_UP]: "bg-indigo-700",
  [OrderStatus.DEPOT_RECEIVE_PENDING]: "bg-purple-500",
  [OrderStatus.DEPOT_RECEIVE_IN_PROGRESS]: "bg-purple-500",
  [OrderStatus.AT_DEPOT]: "bg-purple-500",
  [OrderStatus.DEPOT_SORT_PENDING]: "bg-violet-500",
  [OrderStatus.DEPOT_SORT_IN_PROGRESS]: "bg-violet-500",
  [OrderStatus.SORTED]: "bg-violet-500",
  [OrderStatus.DEPOT_DISPATCH_PENDING]: "bg-cyan-500",
  [OrderStatus.DEPOT_DISPATCH_IN_PROGRESS]: "bg-cyan-500",
  [OrderStatus.IN_TRANSIT]: "bg-cyan-500",
  [OrderStatus.DELIVERY_PENDING]: "bg-yellow-500",
  [OrderStatus.OUT_FOR_DELIVERY]: "bg-yellow-500",
  [OrderStatus.DELIVERED]: "bg-green-700",
  [OrderStatus.RETURN_PENDING]: "bg-orange-500",
  [OrderStatus.RETURN_IN_PROGRESS]: "bg-orange-500",
  [OrderStatus.RETURNED]: "bg-orange-500",
  [OrderStatus.PICKUP_FAILED]: "bg-red-500",
  [OrderStatus.DEPOT_RECEIVE_FAILED]: "bg-red-500",
  [OrderStatus.DEPOT_SORT_FAILED]: "bg-red-500",
  [OrderStatus.DEPOT_DISPATCH_FAILED]: "bg-red-500",
  [OrderStatus.DELIVERY_FAILED]: "bg-red-500",
  [OrderStatus.RETURN_FAILED]: "bg-red-500",
  [OrderStatus.PICKUP_CANCELLED]: "bg-red-700",
  [OrderStatus.DEPOT_RECEIVE_CANCELLED]: "bg-red-700",
  [OrderStatus.DEPOT_SORT_CANCELLED]: "bg-red-700",
  [OrderStatus.DEPOT_DISPATCH_CANCELLED]: "bg-red-700",
  [OrderStatus.DELIVERY_CANCELLED]: "bg-red-700",
  [OrderStatus.RETURN_CANCELLED]: "bg-red-700",
  [OrderStatus.CANCELLED]: "bg-red-700",
  [OrderStatus.PARTIALLY_PICKED_UP]: "bg-indigo-500",
  [OrderStatus.PARTIALLY_DELIVERED]: "bg-green-500",
} as const;

interface CustomerOrdersTableProps {
  onEditOrder: (orderId: string) => void;
}

export function CustomerOrdersTable({ onEditOrder }: CustomerOrdersTableProps) {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedOrders, setSelectedOrders] = useState<string[]>([]);
  const [page, setPage] = useState(1);
  const [limit] = useState(50);
  const [carouselOpen, setCarouselOpen] = useState(false);
  const [carouselImages, setCarouselImages] = useState<PhotoMeta[]>([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [carouselApi, setCarouselApi] = useState<CarouselApi>();
  const [cancelOrderId, setCancelOrderId] = useState<string | null>(null);
  const [cancelAlertOpen, setCancelAlertOpen] = useState(false);
  // Add state to track viewport width and detect mobile views
  const [isMobile, setIsMobile] = useState(false);

  const { data: response } = useCustomerOrders({ page, limit });
  const { printLabels, printBulkLabels } = useOrderLabels();
  const cancelOrder = useOrderStatus();

  // Detect mobile viewport on client side
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768); // 768px is standard md breakpoint
    };

    // Initial check
    checkMobile();

    // Add resize listener
    window.addEventListener("resize", checkMobile);

    // Cleanup
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const filteredOrders = useMemo(() => {
    const orders = response?.data ?? [];
    if (!searchTerm) return orders;

    return orders.filter((order) => {
      const orderNumber = String(order?.orderNumber || "");
      const recipientName = String(order?.recipientName || "");
      const recipientEmail = String(order?.recipientEmail || "");

      return (
        orderNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
        recipientName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        recipientEmail.toLowerCase().includes(searchTerm.toLowerCase())
      );
    });
  }, [response?.data, searchTerm]);

  const handleHeaderCheckboxChange = (checked: boolean) => {
    setSelectedOrders(checked ? filteredOrders.map((order) => order.id) : []);
  };

  const handlePrintLabels = async (orderId: string) => {
    try {
      const blob = await printLabels.mutateAsync(orderId);
      const url = window.URL.createObjectURL(
        new Blob([blob], { type: "application/pdf" })
      );
      // Open PDF in new window
      const newWindow = window.open(url, "_blank");
      // For iOS Safari, we need to delay revoking the URL
      // This gives the browser enough time to open and load the PDF
      setTimeout(() => {
        // Only revoke if the window was successfully opened
        if (newWindow) {
          window.URL.revokeObjectURL(url);
        }
      }, 1000);
    } catch (error) {
      logger.error("Error printing labels:", error);
    }
  };

  const handleBulkPrintLabels = async () => {
    try {
      const blob = await printBulkLabels.mutateAsync(selectedOrders);
      const url = window.URL.createObjectURL(
        new Blob([blob], { type: "application/pdf" })
      );
      // Open PDF in new window
      const newWindow = window.open(url, "_blank");
      // For iOS Safari, we need to delay revoking the URL
      // This gives the browser enough time to open and load the PDF
      setTimeout(() => {
        // Only revoke if the window was successfully opened
        if (newWindow) {
          window.URL.revokeObjectURL(url);
        }
      }, 1000);
    } catch (error) {
      logger.error("Error printing bulk labels:", error);
    }
  };

  const handleCancelOrder = async (orderId: string) => {
    try {
      await cancelOrder.mutateAsync(orderId);
      setCancelOrderId(null);
      setCancelAlertOpen(false);
    } catch (error) {
      logger.error("Error cancelling order:", error);
    }
  };

  const openCancelDialog = (orderId: string) => {
    setCancelOrderId(orderId);
    setCancelAlertOpen(true);
  };

  const handleTrackingClick = (trackingNumber: string) => {
    window.open(`/tracking/${trackingNumber}`, "_blank");
  };

  // Helper functions for getting task data similar to admin-orders-table
  const getTaskProofImages = (tasks: Task[] = []): PhotoMeta[] => {
    return tasks
      .filter(
        (task) =>
          task.proof?.mediaItems &&
          Array.isArray(task.proof.mediaItems) &&
          task.proof.mediaItems.length > 0
      )
      .flatMap((task) =>
        (task.proof?.mediaItems ?? []).map((item: ProofItem) => ({
          url: item.url ?? "",
          taskType: task.type ?? "UNKNOWN",
          status: task.status ?? "UNKNOWN",
          time: task.updatedAt ?? task.createdAt ?? new Date().toISOString(),
          notes: task.proof?.notes,
        }))
      );
  };

  const handleOpenCarousel = (order: CustomerOrder) => {
    // Combine all proof images from tasks
    const tasks: Task[] = order.tasks ?? [];
    const images = getTaskProofImages(tasks);

    if (images.length === 0) {
      return; // Don't open carousel if there are no images
    }

    setCarouselImages(images);
    setCurrentImageIndex(0);
    setCarouselOpen(true);
  };

  // Set up carousel API and handle slide changes
  // This effect runs when the carousel API is available
  useMemo(() => {
    if (!carouselApi) {
      return;
    }

    carouselApi.on("select", () => {
      setCurrentImageIndex(carouselApi.selectedScrollSnap());
    });
  }, [carouselApi]);

  return (
    <div className="space-y-4">
      <div className="flex sm:items-center justify-between sm:flex-row flex-col gap-2">
        <Input
          placeholder="Search orders..."
          className="sm:max-w-sm w-full"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <Button
          onClick={handleBulkPrintLabels}
          disabled={selectedOrders.length === 0}
          className="w-full sm:w-auto"
        >
          <Printer className="h-4 w-4 mr-2" />
          Print Selected Labels ({selectedOrders.length})
        </Button>
      </div>

      {/* Mobile card view */}
      {isMobile ? (
        <div className="space-y-4 px-1">
          {filteredOrders.map((order: CustomerOrder) => (
            <OrderCard
              key={order.id}
              order={order}
              isSelected={selectedOrders.includes(order.id)}
              onSelect={(checked) => {
                setSelectedOrders(
                  checked
                    ? [...selectedOrders, order.id]
                    : selectedOrders.filter((id) => id !== order.id)
                );
              }}
              onPrintLabel={handlePrintLabels}
              onEditOrder={onEditOrder}
              onCancelOrder={openCancelDialog}
              onViewPhotos={handleOpenCarousel}
              onTrackingClick={handleTrackingClick}
              getTaskProofImages={getTaskProofImages}
            />
          ))}
        </div>
      ) : (
        /* Desktop table view */
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="sticky left-0 bg-white min-w-[120px] sm:min-w-[150px] z-10 rounded-md">
                  <div className="flex items-center gap-3">
                    <Checkbox
                      checked={
                        filteredOrders.length > 0 &&
                        selectedOrders.length === filteredOrders.length
                      }
                      onCheckedChange={handleHeaderCheckboxChange}
                      disabled={filteredOrders.length === 0}
                    />
                    <span>Order</span>
                  </div>
                </TableHead>
                <TableHead className="min-w-[200px]">Status</TableHead>
                <TableHead className="min-w-[100px] sm:min-w-[200px]">
                  Recipient
                </TableHead>
                <TableHead className="min-w-[100px] sm:min-w-[400px]">
                  Parcels
                </TableHead>
                <TableHead className="hidden sm:table-cell min-w-[150px]">
                  Total (excl. HST)
                </TableHead>
                <TableHead className="hidden sm:table-cell min-w-[100px]">
                  Photos
                </TableHead>
                <TableHead className="min-w-[40px] sticky right-0 bg-white z-10">
                  Actions
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredOrders.map((order: CustomerOrder) => (
                <TableRow key={order.id}>
                  <TableCell className="sticky left-0 bg-white z-10">
                    <div className="flex items-center gap-3">
                      <Checkbox
                        checked={selectedOrders.includes(order.id)}
                        onCheckedChange={(checked) => {
                          setSelectedOrders(
                            checked
                              ? [...selectedOrders, order.id]
                              : selectedOrders.filter((id) => id !== order.id)
                          );
                        }}
                      />
                      <div>
                        <p className="font-medium">#{order.orderNumber}</p>
                        <p className="text-sm text-gray-500 hidden sm:block">
                          {new Date(order.createdAt).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge
                      className={`${statusColors[order.status] || "bg-gray-500"}`}
                      variant="default"
                    >
                      {order.status.replace(/_/g, " ")}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div>
                      <p className="font-medium">{order.recipientName}</p>
                      <p className="text-sm text-gray-500 hidden sm:block">
                        {order.recipientEmail}
                      </p>
                      <p className="text-sm text-gray-500 hidden sm:block">
                        {order.recipientPhone}
                      </p>
                      <p className="text-xs text-gray-400 hidden sm:block">
                        <a
                          href={`https://maps.google.com/?q=${encodeURIComponent(
                            `${order.deliveryAddress.line1}, ${order.deliveryAddress.city}`
                          )}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 underline"
                        >
                          {order.deliveryAddress.line1},{" "}
                          {order.deliveryAddress.city}
                        </a>
                      </p>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div>
                      <p className="font-medium w-[80px]">
                        {order.parcels.length} parcels
                      </p>
                      {order.parcels.map((parcel: Parcel) => (
                        <p
                          key={parcel.id}
                          className="text-sm text-gray-500 hidden sm:block"
                        >
                          <span
                            className="text-blue-600 hover:text-blue-800 underline cursor-pointer"
                            onClick={() =>
                              handleTrackingClick(parcel.trackingNumber)
                            }
                          >
                            {parcel.trackingNumber}
                          </span>
                          {` (${parcel.dimensions.length}x${parcel.dimensions.width}x${parcel.dimensions.height}cm, ${parcel.weight}kg)`}
                        </p>
                      ))}
                    </div>
                  </TableCell>
                  <TableCell className="hidden sm:table-cell">
                    <div className="font-medium">
                      ${order.totalFees.toFixed(2)}
                    </div>
                  </TableCell>
                  <TableCell className="hidden sm:table-cell">
                    {order.tasks && order.tasks.length > 0 ? (
                      <div>
                        <div className="flex items-center mb-2">
                          <span className="text-sm text-gray-600 font-medium">
                            {getTaskProofImages(order.tasks).length} photos
                          </span>
                        </div>
                        <div className="flex mt-1 space-x-2">
                          {getTaskProofImages(order.tasks).length > 0 ? (
                            <>
                              {getTaskProofImages(order.tasks)
                                .slice(0, 3)
                                .map((image: PhotoMeta, index) => (
                                  <div
                                    key={index}
                                    className="relative w-12 h-12 cursor-pointer"
                                    onClick={() => handleOpenCarousel(order)}
                                  >
                                    <img
                                      src={image.url}
                                      alt={`${image.taskType} proof`}
                                      className="w-full h-full object-cover rounded-md border border-gray-200"
                                      onError={(e) => {
                                        console.error(
                                          `Failed to load image: ${image.url}`
                                        );
                                        (e.target as HTMLImageElement).src =
                                          "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjQiIGhlaWdodD0iMjQiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyIgZmlsbC1ydWxlPSJldmVub2RkIiBjbGlwLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0yNCAyNGgtMjR2LTI0aDI0djI0em0tMi0yMmgtMjB2MjBoMjB2LTIwem0tNC4xMTggMTQuMDY0Yy0uMzk5LjQxOS0uODM0LjY0MS0xLjI5OS42NDEtLjQ2NSAwLS45LS4yMjItMS4yOTktLjY0MS0uNDAxLS40MTgtLjYwMS0uOTAyLS42MDEtMS40NTEgMC0uNTM5LjIwMS0xLjAyNC42MDEtMS40NTIuMzk5LS40MTkuODM0LS42NDEgMS4yOTktLjY0MS40NjUgMCAuOS4yMjIgMS4yOTkuNjQxLjQwMS40MTguNjAxLjkwMi42MDEgMS40NTEgMCAuNTM5LS4yMDEgMS4wMjQtLjYwMSAxLjQ1MnptLTEuMjk5LTExLjA2NGMtMy4wMjEgMC01LjYwNiAxLjA2Ny03Ljc1NCAzLjItMi4xNDkgMi4xMzQtMy4yMjMgNC43MDgtMy4yMjMgNy43MjEgMCAzLjAyMiAxLjA3NCA1LjU5NyAzLjIyMyA3LjcyIDIuMTQ4IDIuMTM0IDQuNzMzIDMuMjAxIDcuNzU0IDMuMjAxIDMuMDIgMCA1LjYwNS0xLjA2NyA3Ljc1My0zLjIgMi4xNDktMi4xMzQgMy4yMjMtNC43MDkgMy4yMjMtNy43MjEgMC0zLjAyMi0xLjA3NC01LjU5Ny0zLjIyMy03LjcyMS0yLjE0OC0yLjEzMy00LjczMy0zLjItNy43NTMtMy4yem0wIDIuMTMzYzIuMzk1IDAgNC40NDIuODQ4IDYuMTQzIDIuNTMzIDEuNyAxLjY4NiAyLjU1IDMuNzI1IDIuNTUgNi4xMTcgMCAyLjM5Mi0uODUgNC40MzEtMi41NSA2LjExNy0xLjcwMSAxLjY4NS0zLjc0OCAyLjUzMy02LjE0MyAyLjUzMy0yLjM5NCAwLTQuNDQyLS44NDgtNi4xNDMtMi41MzMtMS43LTEuNjg2LTIuNTUtMy43MjUtMi41NS02LjExNyAwLTIuMzkyLjg1LTQuNDMxIDIuNTUtNi4xMTcgMS43MDEtMS42ODUgMy43NDktMi41MzMgNi4xNDMtMi41MzN6Ii8+PC9zdmc+";
                                      }}
                                    />
                                  </div>
                                ))}
                              {getTaskProofImages(order.tasks).length > 3 && (
                                <div className="w-12 h-12 bg-gray-100 rounded-md flex items-center justify-center text-gray-500 border border-gray-200">
                                  +{getTaskProofImages(order.tasks).length - 3}
                                </div>
                              )}
                            </>
                          ) : null}
                        </div>
                      </div>
                    ) : (
                      <div className="text-gray-500 flex items-center">
                        <Camera className="h-4 w-4 mr-1" />
                        <span className="text-sm">No photos</span>
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="sticky right-0 bg-white z-10">
                    <div className="flex flex-col items-center space-y-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handlePrintLabels(order.id)}
                        title="Print Labels"
                        disabled={!isOrderEditable(order.status as OrderStatus)}
                      >
                        <Printer className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onEditOrder(order.id)}
                        title="Edit Order"
                        disabled={!isOrderEditable(order.status as OrderStatus)}
                      >
                        <Edit2 className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openCancelDialog(order.id)}
                        disabled={
                          ![
                            OrderStatus.REGISTERED,
                            OrderStatus.PICKUP_PENDING,
                            OrderStatus.OUT_FOR_PICKUP,
                          ].includes(order.status as OrderStatus)
                        }
                        className={"text-red-600"}
                        title="Cancel Order"
                      >
                        <XCircle className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Empty state for no orders */}
      {filteredOrders.length === 0 && (
        <div className="text-center p-8 border rounded-md">
          <p className="text-gray-500">No orders found</p>
        </div>
      )}

      {/* Pagination - simplified for mobile */}
      <div
        className={`flex items-center ${isMobile ? "justify-center gap-4" : "justify-between px-2"}`}
      >
        {!isMobile && (
          <div className="text-sm text-gray-500">
            Showing {(page - 1) * limit + 1} to{" "}
            {Math.min(page * limit, response?.metadata.totalCount ?? 0)} of{" "}
            {response?.metadata.totalCount ?? 0} results
          </div>
        )}
        <div className="flex gap-2">
          <Button
            variant="outline"
            size={isMobile ? "default" : "sm"}
            onClick={() => setPage(page - 1)}
            disabled={!response?.metadata.hasPreviousPage}
          >
            Previous
          </Button>
          {isMobile && <span className="flex items-center">{page}</span>}
          <Button
            variant="outline"
            size={isMobile ? "default" : "sm"}
            onClick={() => setPage(page + 1)}
            disabled={!response?.metadata.hasNextPage}
          >
            Next
          </Button>
        </div>
      </div>

      {/* Image Carousel Dialog - make it more mobile-friendly */}
      <Dialog open={carouselOpen} onOpenChange={setCarouselOpen}>
        <DialogContent className="sm:max-w-5xl p-0 overflow-hidden max-h-[90vh] w-[95vw] sm:w-auto mx-auto">
          <DialogHeader className="p-4 border-b bg-gray-50 relative">
            <div
              className={`flex items-center ${isMobile ? "flex-col items-start gap-2" : "justify-between pr-8"}`}
            >
              <div className="flex flex-row items-center gap-3">
                <DialogTitle className="text-xl">Order Photos</DialogTitle>
                {carouselImages.length > 0 && (
                  <div className="flex items-center gap-2">
                    <Badge
                      className={
                        carouselImages[currentImageIndex]?.taskType === "PICKUP"
                          ? "bg-indigo-600"
                          : "bg-green-600"
                      }
                    >
                      {carouselImages[currentImageIndex]?.taskType}
                    </Badge>
                    <Badge
                      className={
                        statusColors[
                          carouselImages[currentImageIndex]?.status
                        ] || "bg-gray-500"
                      }
                    >
                      {carouselImages[currentImageIndex]?.status.replace(
                        /_/g,
                        " "
                      )}
                    </Badge>
                  </div>
                )}
              </div>
              {carouselImages.length > 0 && (
                <div className={isMobile ? "w-full" : "text-right"}>
                  <p className="text-sm text-gray-500">
                    {new Date(
                      carouselImages[currentImageIndex]?.time
                    ).toLocaleString()}
                  </p>
                  <p className="text-xs text-gray-400">
                    Image {currentImageIndex + 1} of {carouselImages.length}
                  </p>
                </div>
              )}
            </div>
          </DialogHeader>
          <div className="p-6 relative">
            <Carousel
              className="w-full"
              setApi={setCarouselApi}
              opts={{
                startIndex: 0,
                align: "center",
              }}
            >
              <CarouselContent>
                {carouselImages.map((image, index) => (
                  <CarouselItem key={index}>
                    <div className="flex flex-col items-center space-y-4">
                      <div className="relative w-full max-h-[70vh] overflow-hidden rounded-lg">
                        <img
                          src={image.url}
                          alt={`${image.taskType} proof image`}
                          className="mx-auto object-contain max-h-[70vh] w-full"
                          onError={(e) => {
                            console.error(`Failed to load image: ${image.url}`);
                            (e.target as HTMLImageElement).src =
                              "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjQiIGhlaWdodD0iMjQiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyIgZmlsbC1ydWxlPSJldmVub2RkIiBjbGlwLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0yNCAyNGgtMjR2LTI0aDI0djI0em0tMi0yMmgtMjB2MjBoMjB2LTIwem0tNC4xMTggMTQuMDY0Yy0uMzk5LjQxOS0uODM0LjY0MS0xLjI5OS42NDEtLjQ2NSAwLS45LS4yMjItMS4yOTktLjY0MS0uNDAxLS40MTgtLjYwMS0uOTAyLS42MDEtMS40NTEgMC0uNTM5LjIwMS0xLjAyNC42MDEtMS40NTIuMzk5LS40MTkuODM0LS42NDEgMS4yOTktLjY0MS40NjUgMCAuOS4yMjIgMS4yOTkuNjQxLjQwMS40MTguNjAxLjkwMi42MDEgMS40NTEgMCAuNTM5LS4yMDEgMS4wMjQtLjYwMSAxLjQ1MnptLTEuMjk5LTExLjA2NGMtMy4wMjEgMC01LjYwNiAxLjA2Ny03Ljc1NCAzLjItMi4xNDkgMi4xMzQtMy4yMjMgNC43MDgtMy4yMjMgNy43MjEgMCAzLjAyMiAxLjA3NCA1LjU5NyAzLjIyMyA3LjcyIDIuMTQ4IDIuMTM0IDQuNzMzIDMuMjAxIDcuNzU0IDMuMjAxIDMuMDIgMCA1LjYwNS0xLjA2NyA3Ljc1My0zLjIgMi4xNDktMi4xMzQgMy4yMjMtNC43MDkgMy4yMjMtNy43MjEgMC0zLjAyMi0xLjA3NC01LjU5Ny0zLjIyMy03LjcyMS0yLjE0OC0yLjEzMy00LjczMy0zLjItNy43NTMtMy4yem0wIDIuMTMzYzIuMzk1IDAgNC40NDIuODQ4IDYuMTQzIDIuNTMzIDEuNyAxLjY4NiAyLjU1IDMuNzI1IDIuNTUgNi4xMTcgMCAyLjM5Mi0uODUgNC40MzEtMi41NSA2LjExNy0xLjcwMSAxLjY4NS0zLjc0OCAyLjUzMy02LjE0MyAyLjUzMy0yLjM5NCAwLTQuNDQyLS44NDgtNi4xNDMtMi41MzMtMS43LTEuNjg2LTIuNTUtMy43MjUtMi41NS02LjExNyAwLTIuMzkyLjg1LTQuNDMxIDIuNTUtNi4xMTcgMS43MDEtMS42ODUgMy43NDktMi41MzMgNi4xNDMtMi41MzN6Ii8+PC9zdmc+";
                          }}
                        />
                      </div>
                      {image.notes && (
                        <p className="text-sm bg-gray-50 p-2 rounded-md w-full">
                          Note: {image.notes}
                        </p>
                      )}
                    </div>
                  </CarouselItem>
                ))}
              </CarouselContent>

              {/* Fixed position navigation buttons for better mobile visibility */}
              <CarouselPrevious className="absolute left-2 top-1/2 -translate-y-1/2 z-10" />
              <CarouselNext className="absolute right-2 top-1/2 -translate-y-1/2 z-10" />
            </Carousel>
          </div>
        </DialogContent>
      </Dialog>

      {/* Cancel Order Dialog */}
      <AlertDialog open={cancelAlertOpen} onOpenChange={setCancelAlertOpen}>
        <AlertDialogContent
          className={isMobile ? "w-[95%] max-w-md mx-auto" : ""}
        >
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel Order</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to cancel this order? This action cannot be
              undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className={isMobile ? "flex-col space-y-2" : ""}>
            <AlertDialogCancel className={isMobile ? "mt-0 w-full" : ""}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => handleCancelOrder(cancelOrderId ?? "")}
              className={`bg-red-600 hover:bg-red-700 ${isMobile ? "w-full" : ""}`}
            >
              Confirm Cancellation
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
